import React, { Component } from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { connect } from 'react-redux';
import { Colors } from 'modules/theme';
import H2 from 'components/H2';
import H4 from 'components/H4';
// eslint-disable-next-line no-unused-vars
import H5 from 'components/H5';
import Modal from 'components/Modal';
import Col from 'components/Col';
import Row from 'components/Row';
import Icon from 'components/Icon';
import Loader from 'components/Loader';
import ListEmpty from 'components/ListEmpty';
import Input from 'components/InputText';
import ButtonText from 'components/ButtonText';
import { PDFDownloadLink } from '@react-pdf/renderer';
import {
  getSoftenerCurrentState,
  getSoftenerConsumption,
  getSoftenerPressure,
  getSoftenerBypass,
  getSoftenerWaste,
  softenerSetInitialState,
  generateSoftenerCsv,
  getCsvFiles,
  getConsumptionFromInstallSo,
  getSoftenerCurrentPressure,
} from 'actions/index';
import domtoimage from 'dom-to-image';
import { BarChart, Sector, Bar, ResponsiveContainer, Cell } from 'recharts';
import PropTypes from 'prop-types';
import { ReportWS } from './ReportWS';
import { ModalDatePicker } from './ModalDatePicker';
import ModalCsvFiles from './ModalCsvFiles';

const Container = styled.div`
  margin: 0;
  width: 100%;
`;

const GraphContainer = styled.div`
  width: 100%;
  position: relative;
  height: ${props => props.height}px;
`;

const SaltCard = styled.div`
  width: 100%;
  box-sizing: border-box;
  background: #fff;
  border: 1px solid #e8f1f8;
  border-radius: 12px;
  padding: 20px 24px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.05);
`;

const SaltHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
`;

const SaltTitle = styled.h2`
  margin: 0;
  font-size: 18px;
  font-weight: 600;
  color: #333;
`;

const SaltBody = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 24px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
    justify-items: center;
  }
`;

const SaltMetricBlock = styled.div`
  text-align: ${props => (props.$center ? 'center' : 'left')};
`;

const SaltMetricValue = styled.div`
  font-size: 36px;
  font-weight: 700;
  color: ${props => props.$color || Colors.blue};
  line-height: 1.1;
`;

const SaltMetricLabel = styled.div`
  margin-top: 6px;
  font-size: 13px;
  color: #64748b;
`;

const SaltBarTrack = styled.div`
  width: 100%;
  height: 12px;
  background: #eef2f6;
  border-radius: 999px;
  overflow: hidden;
`;

const SaltBarFill = styled.div`
  height: 100%;
  width: ${props => props.$percent}%;
  background: ${props => props.$color};
  border-radius: 999px;
  transition: width 0.3s ease;
`;

// Evaluated per mount, not at module load: a module-level `new Date()` freezes
// at bundle evaluation, so the chart kept asking for data up to whenever the app
// was opened while the total widgets asked for "now", and the two disagreed by
// everything consumed in between.
const getStartDate = () =>
  moment()
    .set({ hour: 0, minute: 0, second: 0, millisecond: 0 })
    .add(-7, 'days')
    .toDate();

const getEndDate = () => moment().toDate();

class WS extends Component {
  constructor(props) {
    super(props);
    this.state = {
      totalWaterChart: '',
      wasteWaterChart: '',
      saltChart: '',
      generatePDF: false,
      downloadPDF: false,
      showConfiguration: false,
      datePickerFromConsumption: getStartDate(),
      datePickerToConsumption: getEndDate(),
      datePickerFromWaste: getStartDate(),
      datePickerToWaste: getEndDate(),
      modalDatePickerConsumption: false,
      modalDatePickerWaste: false,
      rangeConsumption: 'days',
      possibleDayRangesConsumption: ['days'],
      rangeWaste: 'days',
      possibleDayRangesWaste: ['days'],
      modalDatePickerCsv: false,
      datePickerFromCsv: getStartDate(),
      datePickerToCsv: getEndDate(),
      csvType: 1,
      csvTypeString: 'raw_data',
      modalDeviceId: false,
      new_device_id: '',
    };
  }

  componentDidMount() {
    const {
      location: {
        SoftenerFilterHead: { installed_date },
      },
      softenerSetInitialState,
    } = this.props;

    softenerSetInitialState();

    if (moment(getStartDate()) < moment(installed_date)) {
      // eslint-disable-next-line react/no-did-mount-set-state
      this.setState(
        {
          datePickerFromConsumption: moment(installed_date).toDate(),
          datePickerFromCsv: moment(installed_date).toDate(),
          datePickerFromWaste: moment(installed_date).toDate(),
        },
        () => this.getInitialData(),
      );
    } else {
      this.getInitialData();
    }
  }

  componentDidUpdate(prevProps) {
    const { softenerConsumption, softenerConsumptionLoading, updateLastUpdated } = this.props;

    if (
      prevProps.softenerConsumptionLoading &&
      !softenerConsumptionLoading &&
      softenerConsumption &&
      softenerConsumption.last_updated &&
      updateLastUpdated
    ) {
      updateLastUpdated(softenerConsumption.last_updated);
    }
  }

  getInitialData = () => {
    const {
      location: { SoftenerFilterHead },
      getSoftenerCurrentState,
      getSoftenerConsumption,
      getSoftenerPressure,
      getSoftenerWaste,
      getConsumptionFromInstallSo,
      getSoftenerCurrentPressure,
      setLastUpdatedLoading,
    } = this.props;
    const {
      device_id,
      SoftenerConfiguration: {
        Softener: { bypass_key },
      },
      installed_date,
    } = SoftenerFilterHead;

    if (device_id) {
      const {
        datePickerFromConsumption,
        datePickerToConsumption,
        datePickerFromWaste,
        datePickerToWaste,
        rangeWaste,
        rangeConsumption,
      } = this.state;

      if (setLastUpdatedLoading) {
        setLastUpdatedLoading();
      }

      getSoftenerCurrentPressure({ device_id, from: installed_date });
      getSoftenerCurrentState({ device_id, from: installed_date });
      getConsumptionFromInstallSo({ device_id, from: installed_date });

      getSoftenerConsumption({
        device_id,
        from: datePickerFromConsumption,
        to: datePickerToConsumption,
        range: rangeConsumption,
      });

      getSoftenerPressure({
        device_id,
        from: datePickerFromConsumption,
        to: datePickerToConsumption,
        range: rangeConsumption,
      });

      if (bypass_key !== 'N/A') {
        getSoftenerWaste({
          device_id,
          from: datePickerFromWaste,
          to: datePickerToWaste,
          range: rangeWaste,
        });
      }
    }
  };

  handleDate = (value, key) => {
    this.setState({ [key]: value });
  };

  possibleRanges = daysRange => {
    const possibleDayRanges = [];
    if (daysRange <= 1) possibleDayRanges.push('hours');
    if (daysRange >= 1 && daysRange < 15) possibleDayRanges.push('days');
    if (daysRange >= 14 && daysRange < 105) possibleDayRanges.push('weeks');
    if (daysRange >= 30 && daysRange < 450) possibleDayRanges.push('months');
    if (daysRange >= 365) possibleDayRanges.push('years');

    return possibleDayRanges;
  };

  generateSoftenerCsv = () => {
    const {
      generateSoftenerCsv,
      location: {
        id,
        SoftenerFilterHead: { device_id },
      },
    } = this.props;
    const { csvTypeString } = this.state;

    const { datePickerFromCsv: from, datePickerToCsv: to } = this.state;
    generateSoftenerCsv({ device_id, location_id: id, from, to, csvContains: csvTypeString });
  };

  changeCsvType = (csvType, nro) => {
    const csvTypes = ['raw_data', 'calculated_data', 'all_data'];

    this.setState({ [csvType]: nro, csvTypeString: csvTypes[nro - 1] });
  };

  calculeSoftenerConsumption = range => {
    const {
      getSoftenerConsumption,
      getSoftenerPressure,
      location: {
        SoftenerFilterHead: { device_id },
      },
    } = this.props;
    const { datePickerFromConsumption: from, datePickerToConsumption: to } = this.state;
    const fromDate = moment(from);
    const toDate = moment(to);
    const daysRange = toDate.diff(fromDate, 'days');
    const possibleDayRanges = this.possibleRanges(daysRange);

    const currentRange = range || possibleDayRanges[0];
    getSoftenerConsumption({ device_id, from, to, range: currentRange });
    getSoftenerPressure({ device_id, from, to, range: currentRange });

    this.setState({
      possibleDayRangesConsumption: possibleDayRanges,
      rangeConsumption: currentRange,
    });
  };

  calculeSoftenerBypass = range => {
    const {
      getSoftenerBypass,
      location: {
        SoftenerFilterHead: { device_id, installed_date },
      },
    } = this.props;
    const { datePickerFromBypass: from, datePickerToBypass: to } = this.state;

    const fromDate = moment(from);
    const toDate = moment(to);
    const daysRange = toDate.diff(fromDate, 'days');
    const possibleDayRangesBypass = this.possibleRanges(daysRange);

    const currentRange = range || possibleDayRangesBypass[0];
    getSoftenerBypass({ device_id, from, to, installed_date, range: currentRange });
    this.setState({ possibleDayRangesBypass, range: currentRange });
  };

  calculeSoftenerWaste = range => {
    const {
      getSoftenerWaste,
      location: {
        SoftenerFilterHead: { device_id },
      },
    } = this.props;
    const { datePickerFromWaste: from, datePickerToWaste: to } = this.state;
    const fromDate = moment(from);
    const toDate = moment(to);
    const daysRange = toDate.diff(fromDate, 'days');
    const possibleDayRanges = this.possibleRanges(daysRange);

    const currentRange = range || possibleDayRanges[0];
    getSoftenerWaste({ device_id, from, to, range: currentRange });
    this.setState({
      possibleDayRangesWaste: possibleDayRanges,
      rangeWaste: currentRange,
    });
  };

  downloadPDF = () => {
    const {
      totalWaterChart,
      saltChart,
      wasteWaterChart,
      totalGaloonsSum,
      downloadPDF,
      datePickerFromConsumption,
      datePickerToConsumption,
      datePickerFromWaste,
      datePickerToWaste,
    } = this.state;
    const {
      location,
      allAlerts,
      softenerCurrentState,
      softenerConsumption,
      softenerCurrentPressure,
    } = this.props;

    if (downloadPDF) {
      return (
        <PDFDownloadLink
          document={
            <ReportWS
              totalWaterChart={totalWaterChart}
              saltChart={saltChart}
              wasteWaterChart={wasteWaterChart}
              totalGaloonsSum={totalGaloonsSum}
              location={location}
              allAlerts={allAlerts}
              datePickerFromConsumption={datePickerFromConsumption}
              datePickerToConsumption={datePickerToConsumption}
              datePickerFromWaste={datePickerFromWaste}
              datePickerToWaste={datePickerToWaste}
              softenerConsumption={softenerConsumption}
              softenerCurrentState={softenerCurrentState}
              pressure={softenerCurrentPressure}
              saltLevelRemaining={this.calculeDataSaltPercent()}
            />
          }
          fileName="ReportWS.pdf"
        >
          {({ loading }) => (
            <H4
              color={Colors.lightBlue}
              style={{ cursor: 'pointer' }}
              onClick={() => this.setState({ generatePDF: false, downloadPDF: false })}
            >
              <Icon
                name={loading ? 'far fa-spinner' : 'far fa-file-download'}
                color={Colors.lightBlue}
                customStyle={{ marginRight: '5px' }}
              />
              {loading ? 'Loading' : 'Download'} Report
            </H4>
          )}
        </PDFDownloadLink>
      );
    }
    return (
      <Row>
        <Loader size={35} block />
        <H4 color={Colors.lightBlue} marginTop={5}>
          Generating PDF ...
        </H4>
      </Row>
    );
  };

  switchModal = name => {
    const isVisible = this.state[name];
    this.setState({ [name]: !isVisible });
  };

  handleChangeDeviceId = evt => {
    this.setState({ new_device_id: evt.target.value });
  };

  generateReport = async () => {
    try {
      this.setState({ generatePDF: true });

      let totalWaterChart = document.getElementById('totalWaterChart');
      let saltChart = document.getElementById('saltChart');
      let wasteWaterChart = document.getElementById('wasteWaterChart');
      let totalGaloonsSum = document.getElementById('totalGaloonsSum');

      totalWaterChart = totalWaterChart && (await domtoimage.toPng(totalWaterChart));
      saltChart = saltChart && (await domtoimage.toPng(saltChart));
      wasteWaterChart = wasteWaterChart && (await domtoimage.toPng(wasteWaterChart));
      totalGaloonsSum = totalGaloonsSum && (await domtoimage.toPng(totalGaloonsSum));

      this.setState({
        totalWaterChart,
        saltChart,
        wasteWaterChart,
        totalGaloonsSum,
        downloadPDF: true,
      });
    } catch (error) {
      this.setState({ generatePDF: false });
      console.log(error);
    }
  };

  chosseSaltColor = () => {
    const {
      location: {
        SoftenerFilterHead: { FlowMeterOfSofteners },
      },
      softenerCurrentState: { distance },
    } = this.props;
    const { good_salt_level, poor_salt_level } = FlowMeterOfSofteners[0].BrineDrum;

    if (distance <= good_salt_level) {
      return { color: Colors.green, icon: 'check-circle' };
    }
    if (distance > good_salt_level && distance < poor_salt_level) {
      return { color: Colors.yellow, icon: 'exclamation-circle' };
    }
    if (distance >= poor_salt_level) {
      return { color: Colors.red, icon: 'times-circle' };
    }
    return { color: Colors.green, icon: 'check-circle' };
  };

  calculeDataSaltPercent = () => {
    const {
      location: {
        SoftenerFilterHead: { FlowMeterOfSofteners },
      },
      softenerCurrentState: { distance },
    } = this.props;
    const { poor_salt_level } = FlowMeterOfSofteners[0].BrineDrum;
    const saltPercent = +((distance * 100) / poor_salt_level).toFixed(2);
    return (100 - saltPercent).toFixed(2);
  };

  calculeDataSaltGraph = () => {
    const {
      location: {
        SoftenerFilterHead: { FlowMeterOfSofteners },
      },
      softenerCurrentState: { distance },
    } = this.props;
    const { poor_salt_level } = FlowMeterOfSofteners[0].BrineDrum;

    let incomplete = +((distance * 100) / poor_salt_level).toFixed(2);

    let complete = 100 - incomplete >= 0 ? 100 - incomplete : 0;

    if (incomplete >= 100) {
      complete = 100;
      incomplete = 0;
    }

    return [{ name: 'saltLevel', complete, incomplete }];
  };

  renderSaltLevel = () => {
    const {
      softenerCurrentStateLoading,
      softenerCurrentState,
      location: {
        SoftenerFilterHead: { device_id },
      },
    } = this.props;

    const saltVisual = this.chosseSaltColor();
    const remaining = this.calculeDataSaltPercent();
    const saltGraph = this.calculeDataSaltGraph();
    const remainingNumeric = Math.max(0, Math.min(100, Number(remaining) || 0));

    return (
      <SaltCard id="saltChart">
        <SaltHeader>
          <SaltTitle>Salt Level</SaltTitle>
          <Icon name={saltVisual.icon} color={saltVisual.color} fontSize={24} />
        </SaltHeader>

        {softenerCurrentStateLoading ? (
          <Loader size={120} block />
        ) : !device_id ? (
          <ListEmpty message="Installation Not Complete" />
        ) : (
          <SaltBody>
            <div style={{ width: 52, height: 100 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={saltGraph} margin={{ top: 0, right: 0, left: 0, bottom: 0 }}>
                  <Bar
                    dataKey="complete"
                    fill={saltVisual.color}
                    stackId="stack"
                    radius={[4, 4, 0, 0]}
                  >
                    {saltGraph.map((entry, index) => (
                      <Cell key={`complete-${index}`} fill={saltVisual.color} />
                    ))}
                  </Bar>
                  <Bar dataKey="incomplete" fill="#e2e8f0" stackId="stack" radius={[0, 0, 4, 4]} />
                </BarChart>
              </ResponsiveContainer>
            </div>

            <div style={{ width: '100%' }}>
              <SaltMetricBlock>
                <SaltMetricValue $color={saltVisual.color}>{remaining}%</SaltMetricValue>
                <SaltMetricLabel>salt remaining</SaltMetricLabel>
              </SaltMetricBlock>
              <div style={{ marginTop: 16 }}>
                <SaltBarTrack>
                  <SaltBarFill $percent={remainingNumeric} $color={saltVisual.color} />
                </SaltBarTrack>
              </div>
            </div>

            <SaltMetricBlock $center>
              <SaltMetricValue>{softenerCurrentState.distance}</SaltMetricValue>
              <SaltMetricLabel>inches to refill</SaltMetricLabel>
            </SaltMetricBlock>
          </SaltBody>
        )}
      </SaltCard>
    );
  };

  renderActiveShape = props => {
    const { cx, cy, innerRadius, outerRadius, startAngle, endAngle, fill, payload } = props;
    return (
      <g>
        <text
          x={cx}
          y={cy}
          dy={8}
          textAnchor="middle"
          fontFamily="Gotham, Sans Serif"
          fontSize={25}
          fill={Colors.black}
        >
          {100 - payload.value}%
        </text>
        <Sector
          cx={cx}
          cy={cy}
          innerRadius={innerRadius}
          outerRadius={outerRadius}
          startAngle={startAngle}
          endAngle={endAngle}
          fill={fill}
        />
      </g>
    );
  };

  renderPressure = () => {
    const {
      location: {
        SoftenerFilterHead: { device_id },
      },
    } = this.props;

    // if (softenerPressureLoading) {
    //   return <Loader size={200} block />;
    // }

    if (device_id) {
      return (
        <></>
        /* <Col lg={50} md={50} sm={100} xs={100}>
          <H2 marginBottom={10}>Inlet Pressure</H2>
          <Row justifyContent="center">
            {/* <H1 color={Colors.blue} marginRight={15}>
              {softenerPressure}
            </H1>
            <H1 color={Colors.black}>PSI</H1>
            <ListEmpty message="" />
          </Row>
        </Col> */
      );
    }
    return (
      <Col lg={50} md={50} sm={100} xs={100}>
        <H2 marginLeft={10}>Pressure</H2>
        <Col justifyContent="center">
          <ListEmpty message="Installation Not Complete" />
        </Col>
      </Col>
    );
  };

  renderPressureeComented = () => {
    const {
      softenerCurrentPressure,
      softenerCurrentPressureLoading,
      location: {
        SoftenerFilterHead: { device_id },
      },
    } = this.props;

    const iconTime = <Icon name="weight" color={Colors.yellow} fontSize={22} />;
    return (
      <GraphContainer>
        <Row>
          {/* INLET pressure */}
          <Col lg={33} md={33} sm={100} xs={100} divisor>
            <H2 textAlign="center">Inlet Pressure</H2>
            {softenerCurrentPressureLoading ? (
              <Loader size={100} block />
            ) : !device_id ? (
              <Col justifyContent="center">
                <ListEmpty message="Installation Not Complete" />
              </Col>
            ) : (
              <Row>
                <Col lg={100} md={100} sm={100} xs={100}>
                  <H4>
                    <span style={{ marginRight: 20 }}> {iconTime} </span>
                    <span style={{ color: Colors.blue }}>
                      {Math.round(softenerCurrentPressure.inletPressure)}{' '}
                    </span>
                    PSI
                  </H4>
                </Col>
              </Row>
            )}
          </Col>
          {/* OUTLET Pressure */}
          <Col lg={33} md={33} sm={100} xs={100} divisor>
            <H2 textAlign="center">Outlet Pressure</H2>
            {softenerCurrentPressureLoading ? (
              <Loader size={100} block />
            ) : !device_id ? (
              <Col justifyContent="center">
                <ListEmpty message="Installation Not Complete" />
              </Col>
            ) : (
              <Row>
                <Col lg={100} md={100} sm={100} xs={100}>
                  <H4>
                    <span style={{ marginRight: 20 }}> {iconTime} </span>
                    <span style={{ color: Colors.blue }}>
                      {Math.round(softenerCurrentPressure.outletPressure)}{' '}
                    </span>
                    PSI
                  </H4>
                </Col>
              </Row>
            )}
          </Col>
          {/* DIFFERENTIAL Pressure */}
          <Col lg={33} md={33} sm={100} xs={100}>
            <H2 textAlign="center">Differential Pressure</H2>
            {softenerCurrentPressureLoading ? (
              <Loader size={100} block />
            ) : !device_id ? (
              <Col justifyContent="center">
                <ListEmpty message="Installation Not Complete" />
              </Col>
            ) : (
              <Row>
                <Col lg={100} md={100} sm={100} xs={100}>
                  <H4>
                    <span style={{ marginRight: 20 }}> {iconTime} </span>
                    <span style={{ color: Colors.blue }}>
                      {softenerCurrentPressure.inletPressure -
                        softenerCurrentPressure.outletPressure >=
                      0
                        ? Math.round(
                            (
                              softenerCurrentPressure.inletPressure -
                              softenerCurrentPressure.outletPressure
                            ).toFixed(2),
                          )
                        : 0}{' '}
                    </span>
                    PSI
                  </H4>
                </Col>
              </Row>
            )}
          </Col>
        </Row>
      </GraphContainer>
    );
  };

  renderConfigurationModal = () => {
    const { showConfiguration } = this.state;
    const {
      location: {
        SoftenerFilterHead: { SoftenerConfiguration, description },
      },
    } = this.props;
    const { name } = SoftenerConfiguration;

    return (
      <Modal
        title="Configuration"
        onClose={() => this.switchModal('showConfiguration')}
        isVisible={showConfiguration}
      >
        <H4 lineHeight={2} textAlign="left" color={Colors.gray8} weight={325} style={{ margin: 0 }}>
          Configuration name
        </H4>
        <H4 lineHeight={2} color={Colors.black} weight={500} style={{ marginTop: 5 }}>
          {name}
        </H4>
        <H4 lineHeight={2} textAlign="left" color={Colors.gray8} weight={325} style={{ margin: 0 }}>
          Description
        </H4>
        <H4 lineHeight={2} color={Colors.black} weight={500} style={{ marginTop: 5 }}>
          {description}
        </H4>
      </Modal>
    );
  };

  renderConsumptions = () => (
    /*  const {
      location: { SoftenerFilterHead },
      softenerConsumption: { consumption },
      softenerPressure: { pressures },
    } = this.props;
 */
    /*  const CustomTooltip = ({ payload, active }) => {
      if (active && payload && payload[0]) {
        const currentPayload = payload[0].payload;
        return (
          <div
            style={{
              backgroundColor: 'white',
              padding: 1,
              borderRadius: 5,
              borderColor: 'gray',
              borderStyle: 'solid',
              borderWidth: 1,
            }}
          >
            <p style={{ color: 'gray' }}>{currentPayload.tooltipLabel}</p>
            <p style={{ color: 'gray' }}>{Math.round(currentPayload.gal)} Gal</p>
            <p style={{ color: 'gray' }}>
              Max PD: {Math.round(currentPayload.pressureDiferential)} PSI
            </p>
          </div>
        );
      }

      return null;
    }; */

    /*   const dataChart = consumption.map((consumption, index) => ({
      ...pressures[index],
      ...consumption,
    }));
 */
    <></>
    // coming soon
    /* <GraphContainer>
        <Row style={{ minHeight: 60, width: '50%', margin: 'auto' }}>
          <Col xs={100} md={100} sm={40} lg={40}>
            <H4 weight="bold" marginTop={5} style={{ letterSpacing: 1, lineHeight: 1 }}>
              Flow
            </H4>
            <H5 weight="bold" color={Colors.lightBlue} marginTop={5}>
              Period selected
            </H5>
          </Col>
          <Col xs={100} md={100} sm={40} lg={40}>
            <ListEmpty message="" />
          </Col>
        </Row>

        <Col style={{ height: 400 }} justifyContent="center">
          <ResponsiveContainer width="100%" height={285} id="totalWaterChart">
            <ListEmpty message="" />
          </ResponsiveContainer>
        </Col>
      </GraphContainer>/ */
    // end coming soon

    // <>
    //   {softenerConsumptionLoading ? (
    //     <GraphContainer style={{ height: 450 }}>
    //       <Loader size={200} block />
    //     </GraphContainer>
    //   ) : (
    //     <GraphContainer>
    //       <Row style={{ minHeight: 60, width: '50%', margin: 'auto' }}>
    //         <Col xs={100} md={100} sm={40} lg={40}>
    //           <H4 weight="bold" marginTop={5} style={{ letterSpacing: 1, lineHeight: 1 }}>
    //             Flow
    //           </H4>
    //           <H5 weight="bold" color={Colors.lightBlue} marginTop={5}>
    //             Period selected
    //           </H5>
    //         </Col>
    //         <Col xs={100} md={100} sm={40} lg={40}>
    //           <H2 color={Colors.blue}>{total_flow === 0 ? 'N/A' : total_flow}</H2>
    //         </Col>
    //         <Col xs={100} md={100} sm={20} lg={20}>
    //           <H2>gal</H2>
    //         </Col>
    //       </Row>

    //       {softenerPressureLoading && (
    //         <Row style={{ width: 200, margin: 'auto', height: 10 }}>
    //           <Loader size={15} block />
    //           <H5 color={Colors.lightBlue}>Loading max pressures...</H5>
    //         </Row>
    //       )}

    //       <Col style={{ height: 400 }} justifyContent="center">
    //         <ResponsiveContainer width="100%" height={285} id="totalWaterChart">
    //           {!device_id ? (
    //             <Col style={{ height: 400 }} justifyContent="center">
    //               <ListEmpty message="Installation Not Complete" />
    //             </Col>
    //           ) : (
    //             <ComposedChart
    //               data={dataChart}
    //               margin={{ right: 20, left: -15, bottom: 0 }}
    //               barSize={20}
    //             >
    //               <XAxis
    //                 dataKey="name"
    //                 stroke={Colors.gray}
    //                 tick={{ fontSize: 12 }}
    //                 scale="point"
    //                 padding={{ left: 15, top: 15 }}
    //               />
    //               <YAxis type="number" unit="" stroke={Colors.gray} tick={{ fontSize: 12 }} />
    //               <Tooltip content={<CustomTooltip />} />
    //               <CartesianGrid strokeDasharray="3 3" />
    //               <Bar dataKey="gal">
    //                 {consumption.map((entry, index) => (
    //                   <Cell
    //                     key={`cell-${index}`}
    //                     fill={Colors.lightBlue}
    //                     background={{ fill: 'transparent' }}
    //                   />
    //                 ))}
    //               </Bar>
    //               <Line dataKey="pressureDiferential" stroke="#006aff" animationDuration={3000} />
    //             </ComposedChart>
    //           )}
    //         </ResponsiveContainer>
    //         {last_updated && (
    //           <H5 color={Colors.gray8}>
    //             Last updated: {moment.unix(last_updated).format('h:mma MM/DD/YYYY')}
    //           </H5>
    //         )}
    //       </Col>
    //     </GraphContainer>
    //   )}
    // </>
  );

  render() {
    const {
      location: { SoftenerFilterHead, id: location_id },
      renderInstructionsModal,
      getCsvFiles,
      csvFiles,
      csvFilesLoading,
      csvFilesTotal,
      setDeviceId,
    } = this.props;
    const { SoftenerConfiguration, installed_date } = SoftenerFilterHead;
    const { SoftenerInstallSteps } = SoftenerConfiguration;
    const {
      datePickerFromConsumption,
      datePickerToConsumption,
      modalDatePickerConsumption,
      datePickerFromWaste,
      datePickerToWaste,
      modalDatePickerWaste,
      modalDatePickerCsv,
      datePickerFromCsv,
      datePickerToCsv,
      csvType,
      new_device_id,
      modalDeviceId,
    } = this.state;

    return (
      <Container>
        <Row style={{ marginBottom: 20, width: '100%' }}>
          <Col lg={100} md={100} sm={100} xs={100} alignItems="stretch" style={{ width: '100%' }}>
            {this.renderSaltLevel()}
          </Col>
        </Row>

        {/* <BG>{this.renderPressureeComented()}</BG> */}

        <ModalDatePicker
          minDate={moment(installed_date).toDate()}
          isVisible={modalDatePickerConsumption}
          handleDateFrom={value => this.handleDate(value, 'datePickerFromConsumption')}
          handleDateTo={value => this.handleDate(value, 'datePickerToConsumption')}
          switchModal={() => this.switchModal('modalDatePickerConsumption')}
          dateFrom={datePickerFromConsumption}
          dateTo={datePickerToConsumption}
          saveChanges={() => {
            this.switchModal('modalDatePickerConsumption');
            this.calculeSoftenerConsumption();
          }}
        />

        <ModalDatePicker
          minDate={moment(installed_date).toDate()}
          isVisible={modalDatePickerWaste}
          handleDateFrom={value => this.handleDate(value, 'datePickerFromWaste')}
          handleDateTo={value => this.handleDate(value, 'datePickerToWaste')}
          switchModal={() => this.switchModal('modalDatePickerWaste')}
          dateFrom={datePickerFromWaste}
          dateTo={datePickerToWaste}
          changeCsvType={this.changeCsvType}
          csvType={csvType}
          saveChanges={() => {
            this.switchModal('modalDatePickerWaste');
            this.calculeSoftenerWaste();
          }}
        />

        <ModalCsvFiles
          minDate={moment(installed_date).toDate()}
          isVisible={modalDatePickerCsv}
          handleDateFrom={value => this.handleDate(value, 'datePickerFromCsv')}
          handleDateTo={value => this.handleDate(value, 'datePickerToCsv')}
          switchModal={() => this.switchModal('modalDatePickerCsv')}
          dateFrom={datePickerFromCsv}
          dateTo={datePickerToCsv}
          changeCsvType={this.changeCsvType}
          csvType={csvType}
          saveChanges={() => {
            this.switchModal('modalDatePickerCsv');
            this.generateSoftenerCsv();
          }}
          csvApiType="softener"
          locationId={location_id}
          csvFiles={csvFiles}
          csvFilesLoading={csvFilesLoading}
          csvFilesTotal={csvFilesTotal}
          getCsvFiles={getCsvFiles}
        />

        <Modal
          title="Update Device ID (Serial Number)"
          onClose={() => this.switchModal('modalDeviceId')}
          isVisible={modalDeviceId}
          size="small"
        >
          <Row>
            <Col>
              <Input
                placeholder="Device ID"
                value={new_device_id}
                onChange={this.handleChangeDeviceId}
                style={{ marginBottom: '20px' }}
              />
              <ButtonText
                text="Update"
                theme="primary"
                onClick={() =>
                  setDeviceId({ type: 'softener', device_id: new_device_id, location_id })
                }
              />
            </Col>
          </Row>
        </Modal>

        {this.renderConfigurationModal()}
        {renderInstructionsModal(
          this.state.modalViewInstructions,
          () => this.switchModal('modalViewInstructions'),
          SoftenerInstallSteps,
        )}
      </Container>
    );
  }
}

function mapStateToProps(state) {
  return {
    softenerCurrentState: state.locations.softenerCurrentState,
    softenerCurrentStateLoading: state.locations.softenerCurrentStateLoading,
    softenerConsumption: state.locations.softenerConsumption,
    softenerConsumptionLoading: state.locations.softenerConsumptionLoading,
    softenerPressure: state.locations.softenerPressure,
    softenerPressureLoading: state.locations.softenerPressureLoading,
    softenerBypass: state.locations.softenerBypass,
    softenerBypassLoading: state.locations.softenerBypassLoading,
    softenerWaste: state.locations.softenerWaste.waste,
    softenerWasteLoading: state.locations.softenerWasteLoading,
    csvFiles: state.locations.csvFiles,
    csvFilesLoading: state.locations.csvFilesLoading,
    csvFilesTotal: state.locations.csvFilesTotal,
    consumptionFromInstallSoLoading: state.locations.consumptionFromInstallSoLoading,
    consumptionFromInstallSo: state.locations.consumptionFromInstallSo,
    softenerCurrentPressure: state.locations.softenerCurrentPressure,
    softenerCurrentPressureLoading: state.locations.softenerCurrentPressureLoading,
    role_id: state.auth.profile.role_id,
  };
}

function mapDispatchToProps(dispatch) {
  return {
    getSoftenerCurrentState: params => dispatch(getSoftenerCurrentState(params)),
    getSoftenerConsumption: params => dispatch(getSoftenerConsumption(params)),
    getSoftenerPressure: params => dispatch(getSoftenerPressure(params)),
    getSoftenerBypass: params => dispatch(getSoftenerBypass(params)),
    getSoftenerWaste: params => dispatch(getSoftenerWaste(params)),
    softenerSetInitialState: () => dispatch(softenerSetInitialState()),
    generateSoftenerCsv: params => dispatch(generateSoftenerCsv(params)),
    getCsvFiles: params => dispatch(getCsvFiles(params)),
    getConsumptionFromInstallSo: params => dispatch(getConsumptionFromInstallSo(params)),
    getSoftenerCurrentPressure: params => dispatch(getSoftenerCurrentPressure(params)),
  };
}
export default connect(mapStateToProps, mapDispatchToProps)(WS);

/* eslint-disable react/no-unused-prop-types */
WS.propTypes = {
  active: PropTypes.bool,
  allAlerts: PropTypes.array,
  csvFiles: PropTypes.array,
  csvFilesLoading: PropTypes.bool,
  csvFilesTotal: PropTypes.number,
  generateSoftenerCsv: PropTypes.func,
  getConsumptionFromInstallSo: PropTypes.func,
  getCsvFiles: PropTypes.func,
  getSoftenerBypass: PropTypes.func,
  getSoftenerConsumption: PropTypes.func,
  getSoftenerCurrentPressure: PropTypes.func,
  getSoftenerCurrentState: PropTypes.func,
  getSoftenerPressure: PropTypes.func,
  getSoftenerWaste: PropTypes.func,
  location: PropTypes.object,
  payload: PropTypes.object,
  renderInstructionsModal: PropTypes.func,
  role_id: PropTypes.number,
  setDeviceId: PropTypes.func,
  setLastUpdatedLoading: PropTypes.func,
  softenerBypass: PropTypes.object,
  softenerBypassLoading: PropTypes.bool,
  softenerConsumption: PropTypes.object,
  softenerConsumptionLoading: PropTypes.bool,
  softenerCurrentPressure: PropTypes.object,
  softenerCurrentPressureLoading: PropTypes.bool,
  softenerCurrentState: PropTypes.object,
  softenerCurrentStateLoading: PropTypes.bool,
  softenerSetInitialState: PropTypes.func,
  softenerWasteLoading: PropTypes.bool,
  updateLastUpdated: PropTypes.func,
};
